/**
 * F2 — LIVE projection session (browser layer).
 *
 * Two outputs for the same authority (the PRESENTER):
 *
 * 1. **P2P-LAN (two devices)**: reuses the P2P handoff stack —
 *    ephemeral `/api/p2p/*` signaling (BLIND relay: the SDP/ICE envelopes go
 *    encrypted with the one-time key of the AGPJ1 code) + a DIRECT WebRTC
 *    DataChannel between director and projector. Once the channel is open the
 *    room closes and slides travel device-to-device over the LAN without
 *    depending on the internet. The projector can be a browser WITHOUT an
 *    account: it only renders what the director (authorized by the permission
 *    engine on THEIR device) pushes.
 *
 * 2. **Same machine (fallback)**: a second window/tab listening on a
 *    BroadcastChannel — for when the projector is connected by HDMI to the
 *    director's device.
 */

import {
  buildProjectionCode,
  encodeLiveMessage,
  generateTransferKeyBytes,
  importTransferKey,
  parseLiveMessage,
  parseProjectionCode,
  PROJECTION_LIVE_VERSION,
  type ProjectionLiveMessage,
  type TransferChannel,
} from "@agape/shared";
import {
  connectAsReceiver,
  connectAsSender,
  type RtcPeerHandle,
} from "@/features/p2p-transfer/webrtc";
import { createP2pSignalingClient } from "@/features/p2p-transfer/support";

/** Local channel (same machine, second window). */
const LOCAL_PROJECTION_CHANNEL = "agape-projection-local";

/** Output the presenter pushes live messages to. */
export interface LiveOutput {
  send(message: ProjectionLiveMessage): void;
  close(): void;
}

function channelOutput(
  channel: TransferChannel,
  onClosed?: () => void,
  onHello?: () => void,
): LiveOutput {
  channel.onClose(() => onClosed?.());
  // The projector greets on connect (and after reload): we re-push the
  // current slide so it never sits in "waiting".
  channel.onMessage((raw) => {
    const message = parseLiveMessage(raw);
    if (message?.type === "hello") {
      onHello?.();
    }
  });
  return {
    send(message) {
      try {
        channel.send(encodeLiveMessage(message));
      } catch {
        // channel down: the DataChannel's onClose triggers cleanup
      }
    },
    close() {
      try {
        channel.send(encodeLiveMessage({ v: PROJECTION_LIVE_VERSION, type: "bye" }));
      } catch {
        // already closed
      }
    },
  };
}

export interface PresenterLiveSession {
  /** Full AGPJ1 code (room + key) the projector types/scans. */
  code: string;
  ttlSecs: number;
  /** Resolves when the projector connects (DataChannel open). */
  outputPromise: Promise<LiveOutput>;
  /** Tears down the session (polling + RTCPeerConnection). */
  close(): void;
}

/**
 * PRESENTER: creates the room + one-time key and waits for the projector. The
 * resulting code is shown on screen (and never travels to the server with the
 * key).
 */
export async function startPresenterLiveSession(options?: {
  onPeerJoined?: () => void;
  onClosed?: () => void;
  onHello?: () => void;
  timeoutMs?: number;
}): Promise<PresenterLiveSession> {
  const signaling = createP2pSignalingClient();
  const keyBytes = generateTransferKeyBytes();
  const key = await importTransferKey(keyBytes);
  const room = await signaling.createRoom(300);
  const code = buildProjectionCode(room.code, keyBytes);
  const peer: RtcPeerHandle = connectAsSender({
    signaling,
    code: room.code,
    senderToken: room.senderToken,
    key,
    onPeerJoined: options?.onPeerJoined,
    timeoutMs: options?.timeoutMs,
  });
  return {
    code,
    ttlSecs: room.ttlSecs,
    outputPromise: peer.channelPromise.then((channel) =>
      channelOutput(channel, options?.onClosed, options?.onHello),
    ),
    close: () => peer.close(),
  };
}

export interface ProjectorConnection {
  close(): void;
}

/**
 * PROJECTOR: joins with the AGPJ1 code and hands every valid message to
 * `onMessage`. Corrupt messages are dropped (parse → null).
 */
export async function connectProjector(
  rawCode: string,
  onMessage: (message: ProjectionLiveMessage) => void,
  onClosed?: () => void,
): Promise<ProjectorConnection> {
  const parsed = parseProjectionCode(rawCode);
  if (!parsed) {
    throw new Error("invalid-code");
  }
  const signaling = createP2pSignalingClient();
  const key = await importTransferKey(parsed.keyBytes);
  const { receiverToken } = await signaling.joinRoom(parsed.roomCode);
  const peer = connectAsReceiver({
    signaling,
    code: parsed.roomCode,
    receiverToken,
    key,
  });
  const channel = await peer.channelPromise;
  channel.onMessage((raw) => {
    const message = parseLiveMessage(raw);
    if (message) {
      onMessage(message);
    }
  });
  channel.onClose(() => onClosed?.());
  // Hello: the presenter knows the projector is ready.
  channel.send(
    encodeLiveMessage({
      v: PROJECTION_LIVE_VERSION,
      type: "hello",
      role: "projector",
    }),
  );
  return { close: () => peer.close() };
}

/** PRESENTER (same-machine fallback): output over BroadcastChannel. */
export function createLocalOutput(onHello?: () => void): LiveOutput {
  const channel = new BroadcastChannel(LOCAL_PROJECTION_CHANNEL);
  const handler = (event: MessageEvent) => {
    const message = typeof event.data === "string" ? parseLiveMessage(event.data) : null;
    if (message?.type === "hello") {
      onHello?.();
    }
  };
  channel.addEventListener("message", handler);
  return {
    send(message) {
      // oxlint-disable-next-line unicorn/require-post-message-target-origin -- BroadcastChannel.postMessage does not accept targetOrigin (rule intended for window.postMessage)
      channel.postMessage(encodeLiveMessage(message));
    },
    close() {
      const bye = encodeLiveMessage({
        v: PROJECTION_LIVE_VERSION,
        type: "bye",
      });
      // oxlint-disable-next-line unicorn/require-post-message-target-origin -- BroadcastChannel.postMessage does not accept targetOrigin
      channel.postMessage(bye);
      channel.removeEventListener("message", handler);
      channel.close();
    },
  };
}

/** PROJECTOR (same-machine fallback): listens on the BroadcastChannel. */
export function listenLocalProjection(
  onMessage: (message: ProjectionLiveMessage) => void,
): () => void {
  const channel = new BroadcastChannel(LOCAL_PROJECTION_CHANNEL);
  const handler = (event: MessageEvent) => {
    const message = typeof event.data === "string" ? parseLiveMessage(event.data) : null;
    if (message) {
      onMessage(message);
    }
  };
  channel.addEventListener("message", handler);
  // Hello: if the presenter is already live, re-push the current slide.
  const hello = encodeLiveMessage({
    v: PROJECTION_LIVE_VERSION,
    type: "hello",
    role: "projector",
  });
  // oxlint-disable-next-line unicorn/require-post-message-target-origin -- BroadcastChannel.postMessage does not accept targetOrigin
  channel.postMessage(hello);
  return () => {
    channel.removeEventListener("message", handler);
    channel.close();
  };
}
